/* ExpressiveAnnotations.ts
 * Client-side component of ExpressiveAnnotations - annotation-based conditional validation library.
 * https://github.com/jwaliszko/ExpressiveAnnotations
 *
 * TypeScript port for use with aspnet-client-validation (no jQuery dependency).
 * https://github.com/haacked/aspnet-client-validation
 *
 * Copyright (c) 2014 Jarosław Waliszko
 * Licensed MIT: http://opensource.org/licenses/MIT */

import type { ValidationService, ValidatableElement } from "aspnet-client-validation";

interface EASettings
{
	debug: boolean;
	optimize: boolean;
	enumsAsNumbers: boolean;
	registerAllMethods: boolean;
	dependencyTriggers: string | null | undefined;
}

interface ParseError
{
	error: true;
	msg: string;
}

function IsParseError(value: unknown): value is ParseError
{
	return value !== null && typeof value === "object" && (value as ParseError).error === true;
}

interface ParsedParams
{
	expression: string;
	fieldsMap: Record<string, string>;
	constsMap: Record<string, unknown>;
	enumsMap: Record<string, unknown>;
	methodsList: string[];
	parsersMap: Record<string, string>;
	errFieldsMap: Record<string, string>;
	allowEmpty: boolean;
}

interface ComputeResult
{
	valid: boolean;
	condition: boolean | undefined;
}

export class ExpressiveAnnotations
{
	public Settings: EASettings = {
		debug: false,
		optimize: true,
		enumsAsNumbers: true,
		registerAllMethods: false,
		dependencyTriggers: "change keyup"
	};

	private readonly Service: ValidationService;
	private readonly Methods: Record<string, (...args: unknown[]) => unknown> = {};
	private readonly Parsers: Record<string, (value: unknown, field: string) => unknown> = {};
	private readonly ReferencesMap: Record<string, string[]> = {};
	private readonly FieldListeners = new WeakMap<HTMLElement, Array<() => void>>();
	private readonly ValidatedForms = new WeakSet<HTMLFormElement>();

	private readonly Annotations = " abcdefghijklmnopqrstuvwxyz";

	constructor(service: ValidationService)
	{
		this.Service = service;
		this.InitializeMethods();
		this.RegisterProviders();
	}

	public AddMethod(name: string, func: (...args: unknown[]) => unknown): void
	{
		this.AddMethodOverload(name, func);
	}

	public AddValueParser(name: string, func: (value: unknown, field: string) => unknown): void
	{
		name.split(/\s+/).forEach(parser =>
		{
			if (/\S/.test(parser))
			{
				this.Parsers[parser] = func;
			}
		});
	}

	public ApplySettings(options: Partial<EASettings>): void
	{
		Object.assign(this.Settings, options);
		this.VerifySettings();

		document.querySelectorAll("form").forEach(form =>
		{
			this.RemoveFormFieldListeners(form as HTMLFormElement);
			this.BindFields(form as HTMLFormElement, true);
		});

		this.LogInfo(this.StringFormat("EA settings applied:\n{0}", options));
	}

	// ========== Logging ==========

	private LogInfo(message: string): void
	{
		if (this.Settings.debug && typeof console.log === "function")
		{
			console.log("[info] " + this.LogPrep(message, new Date()));
		}
	}

	private LogWarn(message: string): void
	{
		if (typeof console.warn === "function")
		{
			console.warn("[warn] " + this.LogPrep(message, new Date()));
		}
	}

	private LogFail(message: string): void
	{
		if (typeof console.error === "function")
		{
			console.error("[fail] " + this.LogPrep(message, new Date()));
		}
	}

	private LogPrep(message: string, date?: Date): string
	{
		const parsed = this.StringTryParse(message);
		const msg = IsParseError(parsed) ? message : (parsed as string);
		const lines = msg.split("\n");
		const stamp = date !== undefined && date !== null ? "(" + this.DateTimeStamp(date) + "): " : "";
		const fline = stamp + lines.shift();
		return lines.length > 0
			? fline + "\n" + this.StringIndent(lines.join("\n"), 19)
			: fline!;
	}

	// ========== Settings Verification ==========

	private VerifySettings(): void
	{
		if (!this.IsBool(this.Settings.debug))
		{
			throw "EA settings error: debug value must be a boolean (true or false)";
		}
		if (!this.IsBool(this.Settings.optimize))
		{
			throw "EA settings error: optimize value must be a boolean (true or false)";
		}
		if (!this.IsBool(this.Settings.enumsAsNumbers))
		{
			throw "EA settings error: enumsAsNumbers value must be a boolean (true or false)";
		}
		if (!this.IsBool(this.Settings.registerAllMethods))
		{
			throw "EA settings error: registerAllMethods value must be a boolean (true or false)";
		}
		if (!this.IsString(this.Settings.dependencyTriggers)
			&& this.Settings.dependencyTriggers !== null
			&& this.Settings.dependencyTriggers !== undefined)
		{
			throw "EA settings error: dependencyTriggers value must be a string (multiple event types can be bound at once by including each one separated by a space), null or undefined";
		}
	}

	// ========== Type Helpers ==========

	private IsString(value: unknown): value is string
	{
		return typeof value === "string" || value instanceof String;
	}

	private IsBool(value: unknown): value is boolean
	{
		return typeof value === "boolean" || value instanceof Boolean;
	}

	private IsNumeric(value: unknown): value is number
	{
		return typeof value === "number" && !isNaN(value);
	}

	private IsDate(value: unknown): value is Date
	{
		return value instanceof Date;
	}

	private IsObject(value: unknown): boolean
	{
		return typeof value === "object" || value instanceof Object;
	}

	private IsGuid(value: unknown): boolean
	{
		return typeof value === "string"
			&& /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
	}

	private IsArray(value: unknown): value is unknown[]
	{
		return Object.prototype.toString.call(value) === "[object Array]";
	}

	private IsTimeSpan(value: unknown): boolean
	{
		return typeof value === "string"
			&& /(\-)?(?:(\d*)\.)?(\d+)\:(\d+)(?:\:(\d+)\.?(\d{3})?)?/.test(value); // regex for recognition of .NET style timespan string, taken from moment.js v2.9.0
	}

	// ========== String Helpers ==========

	private StringFormat(text: string, ...args: unknown[]): string
	{
		const makeParam = (value: unknown): string =>
		{
			const replacer = (_key: string, val: unknown): unknown =>
				typeof val === "function" ? "function(...) {...}" : val;
			const serialized = this.IsObject(value)
				? this.Settings.registerAllMethods
					? JSON.stringify(value, undefined, 4) // do not print all of the methods not to disturb the console output
					: JSON.stringify(value, replacer, 4)
				: value;
			const str = this.IsString(serialized) ? (serialized as string).replace(/\$/g, "$$$$") : serialized;
			return String(str);
		};

		const applyParam = (t: string, param: string, idx: number): string =>
			t.replace(new RegExp("\\{" + idx + "\\}", "gm"), param);

		for (let i = 0; i < args.length; i++)
		{
			text = applyParam(text, makeParam(args[i]), i);
		}
		return text;
	}

	private StringIndent(str: string, spaces: number): string
	{
		const indent = Array((spaces || 0) + 1).join(" ");
		return str.replace(/^/gm, indent);
	}

	private StringTryParse(value: unknown): string | ParseError
	{
		if (this.IsString(value))
		{
			return value as string;
		}
		if (value !== undefined && value !== null)
		{
			return String(value);
		}
		return { error: true, msg: "Given value was not recognized as a valid string." };
	}

	// ========== Type Parsers ==========

	private BoolTryParse(value: unknown): boolean | ParseError
	{
		if (this.IsBool(value))
		{
			return value as boolean;
		}
		if (this.IsString(value))
		{
			const trimmed = (value as string).trim().toLowerCase();
			if (trimmed === "true" || trimmed === "false")
			{
				return trimmed === "true";
			}
		}
		return { error: true, msg: "Given value was not recognized as a valid boolean." };
	}

	private NumberTryParse(value: unknown): number | ParseError
	{
		const isNumber = (n: unknown): boolean =>
			this.IsNumeric(parseFloat(n as string)) && isFinite(n as number);

		if (isNumber(value))
		{
			return parseFloat(value as string);
		}
		return { error: true, msg: "Given value was not recognized as a valid number." };
	}

	private TimeSpanTryParse(value: unknown): number | ParseError
	{
		if (this.IsTimeSpan(value))
		{
			const DAY = 2, HOUR = 3, MINUTE = 4, SECOND = 5, MILLISECOND = 6;
			const match = /(\-)?(?:(\d*)\.)?(\d+)\:(\d+)(?:\:(\d+)\.?(\d{3})?)?/.exec(value as string)!;
			const sign = (match[1] === "-") ? -1 : 1;
			const parseNum = (v: string | undefined): number =>
			{
				const result = this.NumberTryParse(v || 0);
				return IsParseError(result) ? 0 : (result as number);
			};
			const d = {
				days: parseNum(match[DAY]) * sign,
				hours: parseNum(match[HOUR]) * sign,
				minutes: parseNum(match[MINUTE]) * sign,
				seconds: parseNum(match[SECOND]) * sign,
				milliseconds: parseNum(match[MILLISECOND]) * sign
			};
			return d.milliseconds +
				d.seconds * 1e3 +   // 1000
				d.minutes * 6e4 +   // 1000 * 60
				d.hours * 36e5 +    // 1000 * 60 * 60
				d.days * 864e5;     // 1000 * 60 * 60 * 24
		}
		return { error: true, msg: "Given value was not recognized as a valid .NET style timespan string." };
	}

	private DateTimeTryParse(value: unknown): number | ParseError
	{
		if (this.IsDate(value))
		{
			return (value as Date).getTime();
		}
		if (this.IsString(value))
		{
			const millisec = Date.parse(value as string); // default parsing of string representing an RFC 2822 or ISO 8601 date
			if (this.IsNumeric(millisec))
			{
				return millisec;
			}
		}
		return { error: true, msg: "Given value was not recognized as a valid RFC 2822 or ISO 8601 date." };
	}

	private GuidTryParse(value: unknown): string | ParseError
	{
		if (this.IsGuid(value))
		{
			return (value as string).toUpperCase();
		}
		return { error: true, msg: "Given value was not recognized as a valid guid - guid should contain 32 digits with 4 dashes (xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)." };
	}

	private ObjectTryParse(value: unknown): unknown | ParseError
	{
		try
		{
			return JSON.parse(value as string);
		}
		catch (ex)
		{
			return { error: true, msg: "Given value was not recognized as a valid JSON. " + ex };
		}
	}

	private EnumerationTryParse(value: unknown): number | string | ParseError
	{
		return this.Settings.enumsAsNumbers ? this.NumberTryParse(value) : this.StringTryParse(value);
	}

	private TryParse(value: unknown, type: string, field: string, parser: string | null): unknown
	{
		if (parser !== null && parser !== undefined)
		{
			const parseFunc = this.FindValueParser(field, parser); // pointed by attribute custom field-specific parser lookup - highest parsing priority
			if (!IsParseError(parseFunc))
			{
				return (parseFunc as (v: unknown, f: string) => unknown)(value, field);
			}
			this.LogWarn((parseFunc as ParseError).msg);
		}
		const typedFunc = this.FindValueParser(field, type); // custom type-specific parser lookup - secondary parsing priority
		if (!IsParseError(typedFunc))
		{
			this.LogWarn(this.StringFormat(
				"Overridden {0} type parsing runs for {1} field. All fields of {0} type are going to be parsed using your value parser. " +
				"If such a behavior is unintentional, change the name of your value parser to one, which does not indicate at {0} (or any other) type name.",
				type, field
			));
			return (typedFunc as (v: unknown, f: string) => unknown)(value, field);
		}
		return this.TryAutoParse(value, type); // built-in parser lookup - lowest parsing priority
	}

	private TryAutoParse(value: unknown, type: string): unknown
	{
		switch (type)
		{
			case "string": return this.StringTryParse(value);
			case "bool": return this.BoolTryParse(value);
			case "number": return this.NumberTryParse(value);
			case "timespan": return this.TimeSpanTryParse(value);
			case "datetime": return this.DateTimeTryParse(value);
			case "guid": return this.GuidTryParse(value);
			case "enumeration": return this.EnumerationTryParse(value);
			default: return this.ObjectTryParse(value);
		}
	}

	private FindValueParser(field: string, parser: string): ((value: unknown, field: string) => unknown) | ParseError
	{
		const parseFunc = this.Parsers[parser]; // custom parsing lookup
		if (typeof parseFunc === "function")
		{
			return parseFunc;
		}
		return {
			error: true,
			msg: this.StringFormat(
				"Custom value parser {0} not found. Consider its registration with ea.addValueParser(), or remove redundant ValueParser attribute from {1} model field.",
				parser, field
			)
		};
	}

	// ========== DateTime Stamp ==========

	private DateTimeStamp(date: Date): string
	{
		const pad = (n: number) => ("0" + n).slice(-2);
		return pad(date.getHours()) + ":" + pad(date.getMinutes()) + ":" + pad(date.getSeconds());
	}

	// ========== Toolchain ==========

	private AddMethodOverload(name: string, func: (...args: unknown[]) => unknown): void
	{ // add multiple function signatures to methods object (methods overloading, based only on numbers of arguments)
		const old = this.Methods[name];
		this.Methods[name] = function (...args: unknown[])
		{
			if (func.length === args.length)
			{
				return func(...args);
			}
			if (typeof old === "function")
			{
				return old(...args);
			}
			return func(...args); // no exact signature match, most likely variable number of arguments is accepted
		};
	}

	private RegisterMethods(model: Record<string, unknown>, essentialMethods: string[], fieldName: string): void
	{ // built-in methods are initialized once in the constructor (the original called initialize() here on each invocation, but idempotently)
		if (this.Settings.registerAllMethods)
		{
			for (const name in this.Methods)
			{
				if (Object.prototype.hasOwnProperty.call(this.Methods, name))
				{
					if (Object.prototype.hasOwnProperty.call(model, name))
					{
						this.LogWarn(this.StringFormat(
							"Field {0} - skipping {1}(...) method registration, naming conflict with the field identifier.",
							fieldName, name
						));
						continue;
					}
					model[name] = this.Methods[name];
				}
			}
			return;
		}

		for (const name of essentialMethods)
		{
			if (Object.prototype.hasOwnProperty.call(this.Methods, name))
			{
				model[name] = this.Methods[name];
			}
		}
	}

	private InitializeMethods(): void
	{
		this.AddMethodOverload("Now", () => Date.now()); // return milliseconds
		this.AddMethodOverload("Today", () => new Date(new Date().setHours(0, 0, 0, 0)).getTime()); // return milliseconds
		this.AddMethodOverload("ToDate", (dateString: unknown) => Date.parse(dateString as string)); // return milliseconds
		this.AddMethodOverload("Date", (year: unknown, month: unknown, day: unknown) => // months are 1-based, return milliseconds
			new Date(new Date(year as number, (month as number) - 1, day as number).setFullYear(year as number)).getTime()
		);
		this.AddMethodOverload("Date", (year: unknown, month: unknown, day: unknown, hour: unknown, minute: unknown, second: unknown) => // months are 1-based, return milliseconds
			new Date(new Date(year as number, (month as number) - 1, day as number, hour as number, minute as number, second as number).setFullYear(year as number)).getTime()
		);
		this.AddMethodOverload("TimeSpan", (days: unknown, hours: unknown, minutes: unknown, seconds: unknown) => // return milliseconds
			(seconds as number) * 1e3 + (minutes as number) * 6e4 + (hours as number) * 36e5 + (days as number) * 864e5
		);
		this.AddMethodOverload("Length", (str: unknown) =>
			str !== null && str !== undefined ? (str as string).length : 0
		);
		this.AddMethodOverload("Trim", (str: unknown) =>
			str !== null && str !== undefined ? (str as string).trim() : null
		);
		this.AddMethodOverload("Concat", (strA: unknown, strB: unknown) => [strA, strB].join(""));
		this.AddMethodOverload("Concat", (strA: unknown, strB: unknown, strC: unknown) => [strA, strB, strC].join(""));
		this.AddMethodOverload("CompareOrdinal", (strA: unknown, strB: unknown) =>
		{
			if (strA === strB) return 0;
			if (strA !== null && strB === null) return 1;
			if (strA === null && strB !== null) return -1;
			return (strA as string) > (strB as string) ? 1 : -1;
		});
		this.AddMethodOverload("CompareOrdinalIgnoreCase", (strA: unknown, strB: unknown) =>
		{
			const a = strA !== null && strA !== undefined ? (strA as string).toLowerCase() : null;
			const b = strB !== null && strB !== undefined ? (strB as string).toLowerCase() : null;
			return this.Methods["CompareOrdinal"](a, b);
		});
		this.AddMethodOverload("StartsWith", (str: unknown, prefix: unknown) =>
			str !== null && str !== undefined
			&& prefix !== null && prefix !== undefined
			&& (str as string).slice(0, (prefix as string).length) === (prefix as string)
		);
		this.AddMethodOverload("StartsWithIgnoreCase", (str: unknown, prefix: unknown) =>
		{
			const s = str !== null && str !== undefined ? (str as string).toLowerCase() : null;
			const p = prefix !== null && prefix !== undefined ? (prefix as string).toLowerCase() : null;
			return this.Methods["StartsWith"](s, p);
		});
		this.AddMethodOverload("EndsWith", (str: unknown, suffix: unknown) =>
			str !== null && str !== undefined
			&& suffix !== null && suffix !== undefined
			&& (str as string).slice(-(suffix as string).length) === (suffix as string)
		);
		this.AddMethodOverload("EndsWithIgnoreCase", (str: unknown, suffix: unknown) =>
		{
			const s = str !== null && str !== undefined ? (str as string).toLowerCase() : null;
			const sf = suffix !== null && suffix !== undefined ? (suffix as string).toLowerCase() : null;
			return this.Methods["EndsWith"](s, sf);
		});
		this.AddMethodOverload("Contains", (str: unknown, substr: unknown) =>
			str !== null && str !== undefined
			&& substr !== null && substr !== undefined
			&& (str as string).indexOf(substr as string) > -1
		);
		this.AddMethodOverload("ContainsIgnoreCase", (str: unknown, substr: unknown) =>
		{
			const s = str !== null && str !== undefined ? (str as string).toLowerCase() : null;
			const sub = substr !== null && substr !== undefined ? (substr as string).toLowerCase() : null;
			return this.Methods["Contains"](s, sub);
		});
		this.AddMethodOverload("IsNullOrWhiteSpace", (str: unknown) =>
			str === null || !/\S/.test(str as string)
		);
		this.AddMethodOverload("IsDigitChain", (str: unknown) =>
			/^[0-9]+$/.test(str as string)
		);
		this.AddMethodOverload("IsNumber", (str: unknown) =>
			// taken from PhoneAttribute implementation
			/^[+-]?(?:(?:[0-9]+)|(?:[0-9]+[eE][+-]?[0-9]+)|(?:[0-9]*\.[0-9]+(?:[eE][+-]?[0-9]+)?))$/.test(str as string)
		);
		this.AddMethodOverload("IsEmail", (str: unknown) =>
			// taken from HTML5 specification: http://www.w3.org/TR/html5/forms.html#e-mail-state-(type=email)
			/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(str as string)
		);
		this.AddMethodOverload("IsPhone", (str: unknown) =>
			// taken from PhoneAttribute implementation: https://referencesource.microsoft.com/#System.ComponentModel.DataAnnotations/DataAnnotations/PhoneAttribute.cs
			/^(\+\s?)?((?!\+.*)\(\+?\d+([\s\-\.]?\d+)?\)|\d+)([\s\-\.]?(\(\d+([\s\-\.]?\d+)?\)|\d+))*(\s?(x|ext\.?)\s?\d+)?$/.test(str as string)
		);
		this.AddMethodOverload("IsUrl", (str: unknown) =>
			// contributed by Diego Perini: https://gist.github.com/dperini/729294
			/^(?:(?:https?|ftp):\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:\/\S*)?$/i.test(str as string)
		);
		this.AddMethodOverload("IsRegexMatch", (str: unknown, regex: unknown) =>
			str !== null && str !== undefined
			&& regex !== null && regex !== undefined
			&& new RegExp(regex as string).test(str as string)
		);
		this.AddMethodOverload("Guid", (str: unknown) =>
		{
			const guid = this.GuidTryParse(str);
			if (IsParseError(guid))
			{
				throw guid.msg;
			}
			return guid;
		});
		this.AddMethodOverload("Min", (...values: unknown[]) =>
		{ // accepts both, array and variable number of arguments
			if (values.length === 0) throw "no arguments";
			if (values.length === 1 && this.IsArray(values[0]))
			{
				if ((values[0] as unknown[]).length === 0) throw "empty sequence";
				return Math.min(...(values[0] as number[]));
			}
			return Math.min(...(values as number[]));
		});
		this.AddMethodOverload("Max", (...values: unknown[]) =>
		{ // accepts both, array and variable number of arguments
			if (values.length === 0) throw "no arguments";
			if (values.length === 1 && this.IsArray(values[0]))
			{
				if ((values[0] as unknown[]).length === 0) throw "empty sequence";
				return Math.max(...(values[0] as number[]));
			}
			return Math.max(...(values as number[]));
		});
		this.AddMethodOverload("Sum", (...values: unknown[]) =>
		{ // accepts both, array and variable number of arguments
			if (values.length === 0) throw "no arguments";
			let sum = 0;
			if (values.length === 1 && this.IsArray(values[0]))
			{
				if ((values[0] as unknown[]).length === 0) throw "empty sequence";
				for (const v of (values[0] as unknown[])) { sum += parseFloat(v as string); }
				return sum;
			}
			for (const v of values) { sum += parseFloat(v as string); }
			return sum;
		});
		this.AddMethodOverload("Average", (...values: unknown[]) =>
		{ // accepts both, array and variable number of arguments
			if (values.length === 0) throw "no arguments";
			if (values.length === 1 && this.IsArray(values[0]))
			{
				const arr = values[0] as unknown[];
				if (arr.length === 0) throw "empty sequence";
				return (this.Methods["Sum"](arr) as number) / arr.length;
			}
			return (this.Methods["Sum"](...values) as number) / values.length;
		});
	}

	// ========== Model Helper ==========

	private GetPrefix(str: string | null | undefined): string
	{
		return (str !== undefined && str !== null) ? str.substring(0, str.lastIndexOf(".") + 1) : "";
	}

	private ExtractValue(form: HTMLFormElement, name: string, prefix: string, type: string, parser: string | null): unknown
	{
		const fieldName = prefix + name;
		const fields = Array.from(
			form.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
				`input[name="${CSS.escape(fieldName)}"], select[name="${CSS.escape(fieldName)}"], textarea[name="${CSS.escape(fieldName)}"]`
			)
		);

		if (fields.length === 0)
		{
			throw this.StringFormat("DOM field {0} not found.", fieldName);
		}

		let rawValue: unknown;
		const firstField = fields[0];

		if (firstField instanceof HTMLInputElement && firstField.type === "checkbox")
		{
			if (fields.length > 2)
			{
				this.LogWarn(this.StringFormat("DOM field {0} is ambiguous (unless custom value parser is provided).", fieldName));
			}
			rawValue = firstField.checked;
		}
		else if (firstField instanceof HTMLInputElement && firstField.type === "radio")
		{
			const checkedRadio = (fields as HTMLInputElement[]).find(f => f.checked);
			rawValue = checkedRadio ? checkedRadio.value : undefined;
		}
		else
		{
			if (fields.length > 1)
			{
				this.LogWarn(this.StringFormat("DOM field {0} is ambiguous (unless custom value parser is provided).", fieldName));
			}
			rawValue = (firstField as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement).value;
		}

		if (rawValue === null || rawValue === undefined || rawValue === "")
		{ // field value not set
			return null;
		}

		const parsedValue = this.TryParse(rawValue, type, fieldName, parser); // convert field value to required type
		if (parsedValue !== null && parsedValue !== undefined && IsParseError(parsedValue))
		{
			throw this.StringFormat("DOM field {0} value conversion to {1} failed. {2}", fieldName, type, (parsedValue as ParseError).msg);
		}
		return parsedValue;
	}

	private BuildField(fieldName: string, fieldValue: unknown, object: Record<string, unknown>): void
	{
		const arrayPat = /^([a-z_0-9]+)\[([0-9]+)\]$/i;
		const props = fieldName.split(".");
		let parent = object;

		for (let i = 0; i < props.length - 1; i++)
		{
			let prop = props[i];
			const match = arrayPat.exec(prop);
			if (match)
			{
				const arrName = match[1];
				const arrIndex = parseInt(match[2], 10);
				if (!Object.prototype.hasOwnProperty.call(parent, arrName))
				{
					parent[arrName] = {};
				}
				(parent[arrName] as Record<number, unknown>)[arrIndex] = (parent[arrName] as Record<number, unknown>)[arrIndex] || {};
				parent = (parent[arrName] as Record<number, unknown>)[arrIndex] as Record<string, unknown>;
				continue;
			}
			if (!Object.prototype.hasOwnProperty.call(parent, prop))
			{
				parent[prop] = {};
			}
			parent = parent[prop] as Record<string, unknown>;
		}

		const lastProp = props[props.length - 1];
		const endMatch = arrayPat.exec(lastProp);
		if (endMatch)
		{ // our fieldName matches array access pattern i.e. arr[idx]
			const arrayName = endMatch[1];
			const arrayIndex = parseInt(endMatch[2], 10);
			parent[arrayName] = (parent[arrayName] as unknown[]) || [];
			(parent[arrayName] as unknown[])[arrayIndex] = fieldValue;
		}
		else
		{
			parent[lastProp] = fieldValue;
		}
	}

	private DeserializeObject(
		form: HTMLFormElement,
		fieldsMap: Record<string, string>,
		constsMap: Record<string, unknown>,
		enumsMap: Record<string, unknown>,
		parsersMap: Record<string, string>,
		prefix: string
	): Record<string, unknown>
	{
		const model: Record<string, unknown> = {};

		for (const name in fieldsMap)
		{
			if (Object.prototype.hasOwnProperty.call(fieldsMap, name))
			{
				const type = fieldsMap[name];
				const parser = parsersMap[name] || null;
				const value = this.ExtractValue(form, name, prefix, type, parser);
				this.BuildField(name, value, model);
			}
		}

		for (const name in constsMap)
		{
			if (Object.prototype.hasOwnProperty.call(constsMap, name))
			{
				this.BuildField(name, constsMap[name], model);
			}
		}

		for (const name in enumsMap)
		{
			if (Object.prototype.hasOwnProperty.call(enumsMap, name))
			{
				const value = this.Settings.enumsAsNumbers ? enumsMap[name] : name.split(".").pop();
				this.BuildField(name, value, model);
			}
		}

		return model;
	}

	private AdjustGivenValue(value: unknown, element: ValidatableElement, prefix: string, parsersMap: Record<string, string>): unknown
	{
		if (element instanceof HTMLInputElement && element.type === "checkbox")
		{
			value = element.checked; // special treatment for checkbox, because when unchecked, false value should be retrieved instead of undefined
		}

		const field = element.name.replace(prefix, "");
		const parser = parsersMap[field];
		if (parser !== null && parser !== undefined)
		{
			const parseFunc = this.FindValueParser(element.name, parser); // pointed by attribute custom field-specific parser lookup - highest parsing priority
			if (!IsParseError(parseFunc))
			{
				return (parseFunc as (v: unknown, f: string) => unknown)(value, element.name);
			}
			this.LogWarn((parseFunc as ParseError).msg);
		}
		return value;
	}

	private CtxEval(exp: string, ctx: Record<string, unknown>): unknown
	{ // evaluates expression in the scope of context object
		// eslint-disable-next-line no-new-func
		return (new Function("expression", "context", "with(context){return eval(expression)}"))(exp, ctx); // function constructor used on purpose (a hack), for 'with' statement not to collide with strict mode
	}

	// ========== Validation Helper ==========

	private CollectReferences(fields: string[], refField: string, prefix: string): void
	{
		for (const field of fields)
		{
			const name = prefix + field;
			if (name !== refField)
			{
				this.ReferencesMap[name] = this.ReferencesMap[name] || [];
				if (!this.ReferencesMap[name].includes(refField))
				{
					this.ReferencesMap[name].push(refField);
				}
			}
		}
	}

	private ValidateReferences(name: string, form: HTMLFormElement): void
	{
		// only validate if the form has been submitted at least once (mirrors original CJB guard against premature cross-field validation)
		if (!this.ValidatedForms.has(form))
		{
			this.LogInfo(this.StringFormat("Validator has not been triggered, not validating dependencies of {0}.", name));
			return;
		}

		const referencedFields = this.ReferencesMap[name];
		if (referencedFields !== undefined && referencedFields !== null)
		{
			this.LogInfo(this.StringFormat("Validation triggered for the following dependencies of {0} field:\n{1}.", name, referencedFields.join(", ")));
			let i = referencedFields.length;
			while (i--)
			{
				const field = form.querySelector<ValidatableElement>(
					`input[data-val][name="${CSS.escape(referencedFields[i])}"],` +
					`select[data-val][name="${CSS.escape(referencedFields[i])}"],` +
					`textarea[data-val][name="${CSS.escape(referencedFields[i])}"]`
				);
				if (field !== null)
				{
					this.Service.validateField(field);
				}
			}
		}
		else
		{
			this.LogInfo(this.StringFormat("No fields dependent on {0} detected.", name));
		}
	}

	private RemoveFormFieldListeners(form: HTMLFormElement): void
	{
		form.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>("input, select, textarea").forEach(field =>
		{
			const cleanups = this.FieldListeners.get(field);
			if (cleanups)
			{
				cleanups.forEach(cleanup => cleanup());
				this.FieldListeners.delete(field);
			}
		});
	}

	private GetDependencyTriggers(): string[]
	{
		if (!this.Settings.dependencyTriggers)
		{
			return [];
		}
		return this.Settings.dependencyTriggers.split(/\s+/).filter(t => /\S/.test(t));
	}

	private BindFields(form: HTMLFormElement, force?: boolean): void
	{
		const triggers = this.GetDependencyTriggers();
		if (triggers.length === 0) return;

		// track when the form has been submitted/validated at least once so dependency re-validation is not triggered prematurely
		if (!this.ValidatedForms.has(form))
		{
			const markValidated = () => this.ValidatedForms.add(form);
			form.addEventListener("submit", markValidated, { once: true, capture: true });
		}

		form.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>("input, select, textarea").forEach(field =>
		{
			const isBound = this.FieldListeners.has(field);
			if (!force && isBound) return;

			if (isBound)
			{
				const cleanups = this.FieldListeners.get(field)!;
				cleanups.forEach(cleanup => cleanup());
				this.FieldListeners.delete(field);
			}

			const listener: EventListener = (event) =>
			{
				this.LogInfo(this.StringFormat("Dependency validation trigger - {0} event, handled.", (event as Event).type));
				this.ValidateReferences(field.name, form);
			};

			const cleanups = triggers.map(trigger =>
			{
				field.addEventListener(trigger, listener);
				return () => field.removeEventListener(trigger, listener);
			});

			this.FieldListeners.set(field, cleanups);
		});
	}

	// ========== Param Parsing ==========

	private ParseParams(params: Record<string, string>): ParsedParams
	{
		const parseJson = <T>(s: string | undefined, fallback: T): T =>
		{
			try { return s ? JSON.parse(s) as T : fallback; }
			catch { return fallback; }
		};

		return {
			expression: parseJson<string>(params["expression"], params["expression"] || ""),
			fieldsMap: parseJson<Record<string, string>>(params["fieldsmap"], {}),
			constsMap: parseJson<Record<string, unknown>>(params["constsmap"], {}),
			enumsMap: parseJson<Record<string, unknown>>(params["enumsmap"], {}),
			methodsList: parseJson<string[]>(params["methodslist"], []),
			parsersMap: parseJson<Record<string, string>>(params["parsersmap"], {}),
			errFieldsMap: parseJson<Record<string, string>>(params["errfieldsmap"], {}),
			allowEmpty: params["allowempty"] === "true" || params["allowempty"] === "True"
		};
	}

	private GetErrorMessage(element: ValidatableElement, method: string, errFieldsMap: Record<string, string>, form: HTMLFormElement, prefix: string): string
	{
		let message = element.getAttribute(`data-val-${method}`) || "";
		for (const field in errFieldsMap)
		{
			if (Object.prototype.hasOwnProperty.call(errFieldsMap, field))
			{
				const guid = errFieldsMap[field];
				const value = this.ExtractValue(form, field, prefix, "string", null);
				message = message.replace(new RegExp(guid, "g"), String(value ?? ""));
			}
		}
		return message;
	}

	// ========== Core Computation ==========

	private ComputeAssertThat(method: string, value: unknown, element: ValidatableElement, parsed: ParsedParams, form: HTMLFormElement, prefix: string): ComputeResult
	{
		value = this.AdjustGivenValue(value, element, prefix, parsed.parsersMap); // preprocess given value

		if (value !== undefined && value !== null && value !== "")
		{ // check if the field value is set (continue if so, otherwise skip condition verification)
			const model = this.DeserializeObject(form, parsed.fieldsMap, parsed.constsMap, parsed.enumsMap, parsed.parsersMap, prefix);
			this.RegisterMethods(model, parsed.methodsList, element.name);
			const msgTemplate = "Field {0} - {1} expression:\n[{2}]\nto be executed within the following context{3}:\n{4}";
			this.LogInfo(this.StringFormat(msgTemplate, element.name, method, parsed.expression, this.Settings.registerAllMethods ? " (methods not shown)" : "", model));
			const exprVal = this.CtxEval(parsed.expression, model) as boolean;
			return { valid: exprVal, condition: exprVal };
		}
		return {
			valid: true,
			condition: undefined // undefined always when value is not set (computation redundant)
		};
	}

	private ComputeRequiredIf(method: string, value: unknown, element: ValidatableElement, parsed: ParsedParams, form: HTMLFormElement, prefix: string): ComputeResult
	{
		value = this.AdjustGivenValue(value, element, prefix, parsed.parsersMap);

		let exprVal: boolean | undefined = undefined;
		const msgTemplate = "Field {0} - {1} expression:\n[{2}]\nto be executed within the following context{3}:\n{4}";

		if (!this.Settings.optimize)
		{ // no optimization - compute requirement condition (which now may have changed) despite the fact field value may be provided
			const model = this.DeserializeObject(form, parsed.fieldsMap, parsed.constsMap, parsed.enumsMap, parsed.parsersMap, prefix);
			this.RegisterMethods(model, parsed.methodsList, element.name);
			this.LogInfo(this.StringFormat(msgTemplate, element.name, method, parsed.expression, this.Settings.registerAllMethods ? " (methods not shown)" : "", model));
			exprVal = this.CtxEval(parsed.expression, model) as boolean;
		}

		if (value === undefined || value === null || value === "" // check if the field value is not set (undefined, null or empty string treated at client as null at server)
			|| (!/\S/.test(value as string) && !parsed.allowEmpty))
		{
			if (exprVal !== undefined)
			{
				return { valid: !exprVal, condition: exprVal };
			}

			const model = this.DeserializeObject(form, parsed.fieldsMap, parsed.constsMap, parsed.enumsMap, parsed.parsersMap, prefix);
			this.RegisterMethods(model, parsed.methodsList, element.name);
			this.LogInfo(this.StringFormat(msgTemplate, element.name, method, parsed.expression, this.Settings.registerAllMethods ? " (methods not shown)" : "", model));
			exprVal = this.CtxEval(parsed.expression, model) as boolean; // verify requirement, if satisfied => notify (return false)
			return { valid: !exprVal, condition: exprVal };
		}
		return {
			valid: true,
			condition: exprVal // undefined when optimize flag is on and value is not set (computation redundant)
		};
	}

	// ========== Provider Registration ==========

	private RegisterProviders(): void
	{
		// bind requirements first...
		for (const annotation of this.Annotations.split(""))
		{ // it would be ideal to have exactly as many handlers as there are unique annotations, but the number of annotations isn't known until DOM is ready
			const suffix = annotation.trim();
			const requiredMethod = `requiredif${suffix}`;

			this.Service.addProvider(requiredMethod, (value, element, params) =>
			{
				try
				{
					const form = element.closest("form") as HTMLFormElement;
					const prefix = this.GetPrefix(element.name);
					const parsed = this.ParseParams(params);

					this.BindFields(form);
					this.CollectReferences(Object.keys(parsed.fieldsMap), element.name, prefix);

					const result = this.ComputeRequiredIf(requiredMethod, value, element, parsed, form, prefix);

					this.LogInfo(this.StringFormat(
						"Field {0} - {1} outcome: {2}, requirement {3}.",
						element.name,
						requiredMethod,
						result.condition === undefined
							? "requirement expression computation redundant"
							: result.condition ? "required" : "not required",
						result.valid ? "satisfied" : "not satisfied"
					));

					element.dispatchEvent(new CustomEvent("eavalid", {
						bubbles: true,
						detail: ["requiredif", result.valid, parsed.expression, result.condition, this.Annotations.indexOf(suffix)]
					}));

					if (!result.valid)
					{
						return this.GetErrorMessage(element, requiredMethod, parsed.errFieldsMap, form, prefix) || false;
					}
					return true;
				}
				catch (ex)
				{
					this.LogFail(String(ex));
					return true;
				}
			});
		}

		// ...then move to asserts
		for (const annotation of this.Annotations.split(""))
		{
			const suffix = annotation.trim();
			const assertMethod = `assertthat${suffix}`;

			this.Service.addProvider(assertMethod, (value, element, params) =>
			{
				try
				{
					const form = element.closest("form") as HTMLFormElement;
					const prefix = this.GetPrefix(element.name);
					const parsed = this.ParseParams(params);

					this.BindFields(form);
					this.CollectReferences(Object.keys(parsed.fieldsMap), element.name, prefix);

					const result = this.ComputeAssertThat(assertMethod, value, element, parsed, form, prefix);

					this.LogInfo(this.StringFormat(
						"Field {0} - {1} outcome: {2}, assertion {3}.",
						element.name,
						assertMethod,
						result.condition === undefined
							? "assertion expression computation redundant"
							: result.condition ? "expression true" : "expression false",
						result.valid ? "satisfied" : "not satisfied"
					));

					element.dispatchEvent(new CustomEvent("eavalid", {
						bubbles: true,
						detail: ["assertthat", result.valid, parsed.expression]
					}));

					if (!result.valid)
					{
						return this.GetErrorMessage(element, assertMethod, parsed.errFieldsMap, form, prefix) || false;
					}
					return true;
				}
				catch (ex)
				{
					this.LogFail(String(ex));
					return true;
				}
			});
		}
	}
}
